import React, { useMemo, useState } from 'react';
import { useAnalyticsScope } from '../../../hooks/analytics/useAnalyticsScope.tsx';
import { FileSearch } from 'lucide-react';
import { useAuditorWorkpapersPackage } from '../../../hooks/analytics/useAuditorReports.ts';
import { WidgetCard, StatusChip, formatPercent } from '../controller/shared.tsx';

/**
 * Report #38 — Testing Workpapers Package. Per-control workpaper detail view
 * driven by a `control_id` (like the Tester Evidence Checklist's `run_id`
 * picker): a control-id input renders the execution-level summary once,
 * then an attribute-by-attribute findings table. Non-crashing placeholder
 * until a control is chosen. Read-only — no edit actions.
 */
export const WorkpapersPackageView: React.FC = () => {
  const [controlIdInput, setControlIdInput] = useState('');
  const [activeControlId, setActiveControlId] = useState<string | undefined>(undefined);
  // Demo data needs no real id: the backend ignores it and returns a stored variant.
  const isDemo = useAnalyticsScope().dataMode === 'demo';
  const queryId = isDemo ? 'demo' : activeControlId;
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useAuditorWorkpapersPackage(queryId);
  const rows = useMemo(() => data ?? [], [data]);
  const summary = rows[0];

  return (
    <WidgetCard
      title="Workpapers Package"
      description="Execution summary and attribute-level findings for a selected control"
      loading={!!queryId && isLoading}
      error={queryId && isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load the workpapers package for that control.'}
      isEmpty={!!queryId && !isLoading && !isError && rows.length === 0}
      emptyMessage="No testing workpapers found for that control (no current run, or nothing tested yet)."
      updatedAt={queryId ? dataUpdatedAt : undefined}
      onRefresh={queryId ? refetch : undefined}
      isFetching={isFetching}
    >
      <form
        className="flex items-center gap-2 mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          setActiveControlId(controlIdInput.trim() || undefined);
        }}
      >
        <input
          type="text"
          value={controlIdInput}
          onChange={(e) => setControlIdInput(e.target.value)}
          placeholder="Paste a control id (UUID)…"
          className="flex-1 min-w-0 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          disabled={!controlIdInput.trim()}
          className="shrink-0 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Load
        </button>
      </form>

      {!queryId ? (
        <div className="flex flex-col items-center justify-center py-10 text-center text-gray-400">
          <FileSearch className="h-6 w-6 mb-2" />
          <p className="text-sm">Select a control to view its workpapers package.</p>
        </div>
      ) : rows.length > 0 ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide mr-1.5">Overall</span>
                <StatusChip value={summary?.overall_status} />
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide mr-1.5">Conclusion</span>
                <StatusChip value={summary?.conclusion} fallback="Not concluded" />
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide mr-1.5">Confidence</span>
                <span className="font-medium text-gray-900">
                  {summary?.confidence_score != null ? formatPercent(summary.confidence_score * 100) : '—'}
                </span>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide mr-1.5">Preparer</span>
                <span className="text-gray-700">{summary?.preparer_name ?? '—'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide mr-1.5">Reviewer</span>
                <span className="text-gray-700">{summary?.reviewer_name ?? '—'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide mr-1.5">SoD Verified</span>
                <span className="text-gray-700">{summary?.segregation_verified ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>

          <div className="analytics-scroll overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b border-gray-200">
                  <th className="py-2 pr-3">Attribute</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Deviation Rate</th>
                  <th className="py-2 pr-3">Findings Summary</th>
                  <th className="py-2 pr-3">Review Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows
                  .filter((row) => row.attribute_ref != null)
                  .map((row, idx) => (
                    <tr key={`${row.attribute_ref}-${idx}`}>
                      <td className="py-2 pr-3 font-medium text-gray-900">{row.label ?? row.attribute_ref}</td>
                      <td className="py-2 pr-3">
                        <StatusChip value={row.attribute_status} />
                      </td>
                      <td className="py-2 pr-3 text-gray-600">
                        {row.deviation_rate != null ? formatPercent(row.deviation_rate * 100) : '—'}
                      </td>
                      <td className="py-2 pr-3 text-gray-600 max-w-[280px] truncate" title={row.findings_summary ?? undefined}>
                        {row.findings_summary ?? '—'}
                      </td>
                      <td className="py-2 pr-3 text-gray-600 max-w-[220px] truncate" title={row.review_notes ?? undefined}>
                        {row.review_notes ?? '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </WidgetCard>
  );
};

export default WorkpapersPackageView;
